import React, { useState } from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiCheck,
    FiCpu,
    FiPlay,
    FiRotateCcw,
    FiSliders,
    FiVolume2,
    FiX,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { isMonitoringPkg } from '../../utils/appVersions';

// CommandsTab — single-device variants of the bulk command palettes.
//
// Writes go to `mdm/devices/<MAC>/command` (a plain string) for one-shot
// actions, and to specific state fields (`volume_level`, `ota_enabled`)
// for stateful settings — matching the existing convention on /devices.

export default function CommandsTab({ mac, device }) {
    // The Commands tab focuses on live device controls. System Control
    // (Reboot) lives in the page header; OTA toggle + check now and the
    // Take Screenshot / Pull Logs triggers sit on the Overview tab next
    // to their corresponding "Last …" links.
    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AudioDisplayCard mac={mac} device={device} />
            <div className="xl:col-span-2">
                <AppsCard mac={mac} device={device} />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Helpers — every card uses the same write pattern, so collect it once.
// ---------------------------------------------------------------------

async function sendCommand(mac, command) {
    // Match the bulk handler's contract: set the command, mark feedback
    // pending so the dashboard knows a round-trip is in flight. The device
    // flips feedback to "success"/"error" when it's done.
    await update(ref(db, `mdm/devices/${mac}`), {
        command,
        feedback: 'pending',
    });
}

// ---------------------------------------------------------------------
// Card primitives
// ---------------------------------------------------------------------

function CommandCard({ icon, title, description, children }) {
    return (
        <section className="card-chrome p-5">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400 inline-flex items-center gap-2">
                    {icon} {title}
                </h2>
                {description && (
                    <p className="mt-1 text-xs text-gray-500">{description}</p>
                )}
            </header>
            <div className="flex flex-wrap items-center gap-2">{children}</div>
        </section>
    );
}

// Three button variants kept as named functions so usage sites read like
// English: `<ActionButton>Reboot</ActionButton>` etc.
function ActionButton({ onClick, icon, children, tone = 'neutral', disabled = false }) {
    const tones = {
        neutral: 'bg-white/[0.05] hover:bg-white/[0.08] text-gray-100',
        primary: 'bg-brand hover:bg-brand-400 text-white',
        success: 'bg-green-500/15 hover:bg-green-500/25 text-green-400',
        warning: 'bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-400',
        danger:  'bg-red-500/15 hover:bg-red-500/25 text-red-400',
    };
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone]}`}
        >
            {icon}
            {children}
        </button>
    );
}

// ---------------------------------------------------------------------
// 2) Audio & Display — volume slider (0-100 %) + brightness slider (0-255).
// The device reads volume_level as a PERCENT (0-100) and maps it to the
// hardware step count itself, so the slider is 0-100 % — the same scale as
// the bulk "Send Commands / Audio" modal.
// ---------------------------------------------------------------------

function AudioDisplayCard({ mac, device }) {
    return (
        <CommandCard
            icon={<FiSliders />}
            title="Audio & Display"
            description="Slide and click Apply. The device picks up changes within seconds."
        >
            <div className="w-full space-y-4">
                <SliderRow
                    icon={<FiVolume2 />}
                    label="Volume"
                    min={0}
                    max={100}
                    unit="%"
                    initial={device?.volume_level ?? 0}
                    apply={async (val) => {
                        await update(ref(db, `mdm/devices/${mac}`), { volume_level: val });
                        toast.success(`Volume set to ${val}%`);
                    }}
                />
                <SliderRow
                    icon={<FiCpu />}
                    label="Brightness"
                    min={0}
                    max={255}
                    initial={device?.brightnessLevel ?? 128}
                    apply={async (val) => {
                        await update(ref(db, `mdm/devices/${mac}`), { brightnessLevel: val });
                        toast.success(`Brightness set to ${val}`);
                    }}
                />
            </div>
        </CommandCard>
    );
}

function SliderRow({ icon, label, min, max, initial, apply, unit = '' }) {
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);
    const dirty = draft !== initial;

    const onApply = async () => {
        try {
            setSaving(true);
            await apply(draft);
        } catch (err) {
            toast.error(`Failed: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="grid grid-cols-[140px_1fr_auto] items-center gap-3">
            <span className="text-sm text-gray-300 inline-flex items-center gap-1.5">
                {icon} {label}
            </span>
            <div className="flex items-center gap-3">
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={draft}
                    onChange={(e) => setDraft(Number(e.target.value))}
                    className="flex-1 accent-brand"
                />
                <span className="text-sm text-gray-300 font-mono w-12 text-right">{draft}{unit}</span>
            </div>
            <ActionButton onClick={onApply} disabled={!dirty || saving} tone="primary" icon={<FiCheck />}>
                {saving ? 'Saving…' : 'Apply'}
            </ActionButton>
        </div>
    );
}

// ---------------------------------------------------------------------
// Apps — per-installed-app open / restart / close. The bulk page has
// the full install/uninstall/version-change UX; here we keep the live
// controls that operators actually reach for from a single device.
// ---------------------------------------------------------------------

function AppsCard({ mac, device }) {
    const list = device?.app_list || [];
    if (list.length === 0) {
        return (
            <CommandCard icon={<FiPlay />} title="Apps">
                <p className="text-sm text-gray-500">No apps reported.</p>
            </CommandCard>
        );
    }

    const trigger = (verb, pkg) => () =>
        sendCommand(mac, `${verb}:${pkg}`).then(
            () => toast.success(`${verb} ${pkg}`),
            (err) => toast.error(`${verb} failed: ${err.message}`)
        );

    return (
        <CommandCard
            icon={<FiPlay />}
            title="Apps"
            description="Quick controls — for install / uninstall / version change use the bulk page."
        >
            <div className="w-full divide-y divide-white/[0.06]">
                {list.map((app) => {
                    // The monitoring service IS our control channel — closing
                    // it leaves the device deaf to the dashboard. Hide the
                    // Close button for this package so a misclick can't
                    // brick our remote management.
                    // Either identity of the agent (platform line or MDM line).
                    const isMonitoring = isMonitoringPkg(app.packageName);

                    return (
                        <div key={app.packageName || app.appName} className="py-2 grid grid-cols-[1fr_auto] items-center gap-3">
                            <div className="min-w-0">
                                <div className="text-sm text-gray-100 font-medium truncate">{app.appName || app.packageName}</div>
                                <div className="text-xs text-gray-500 font-mono truncate">{app.packageName}</div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <ActionButton onClick={trigger('open', app.packageName)} icon={<FiPlay />} tone="success">
                                    Open
                                </ActionButton>
                                <ActionButton onClick={trigger('restart', app.packageName)} icon={<FiRotateCcw />}>
                                    Restart
                                </ActionButton>
                                {!isMonitoring && (
                                    <ActionButton onClick={trigger('close', app.packageName)} icon={<FiX />} tone="danger">
                                        Close
                                    </ActionButton>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </CommandCard>
    );
}
